import { Controller } from 'react-hook-form'

import { cn } from '@/lib/utils/helpers'
import { DatePicker } from '@/components/ui/DatePicker'
import { PhoneInput } from '@/components/ui/PhoneInput'
import { SearchableSelect } from '@/components/ui/SearchableSelect'

import '../styles/yardiGuestSearchForm.css'

import { FILTER_TEXT_INPUT_CLASS } from '../constants'

import type { YardiGuestSearchFormProps, GuestSearchFieldErrorProps } from '../types'

function GuestSearchFieldError({
  message,
  testId,
}: GuestSearchFieldErrorProps) {
  return (
    <p
      className="mt-1 flex items-start gap-2 text-xs text-red-600"
      data-testid={testId}
      role="alert"
    >
      <i className="fa fa-exclamation-circle fa-lg shrink-0 leading-none" aria-hidden />
      <span>{message}</span>
    </p>
  )
}

export function YardiGuestSearchForm({ guest }: YardiGuestSearchFormProps) {
  const {
    form: f,
    resultsLoading,
    companyOptions,
    propertyOptions,
    readOnlyControl,
    searchCompany,
    searchProperty,
    companySelected,
    filterSubmit,
    resetFilter,
    loadCompany,
    loadProperty,
  } = guest

  const companyLocked = readOnlyControl.includes('company')
  const propertyLocked = readOnlyControl.includes('property')
  /** Disable the whole form only while guest-card search runs (Go / Reset / initial load). */
  const formBusy = resultsLoading

  return (
    <div
      data-testid="yardi-guest-search-form-card"
      className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4"
    >
      <form
        className="space-y-1"
        data-testid="yardi-guest-search-form"
        onSubmit={(e) => {
          e.preventDefault()
          filterSubmit()
        }}
      >
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3 lg:grid-cols-3 xl:grid-cols-4">
          <div
            className={`space-y-1 ${companyLocked ? 'pointer-events-none opacity-70' : ''}`}
            data-testid="yardi-guest-search-filters-company-field"
          >
            <label
              className="text-[11px] font-semibold tracking-wide text-slate-500 uppercase"
              data-testid="yardi-guest-search-filters-company-label"
            >
              Company
            </label>
            <div className="relative pill-filter-wrapper-vertical">
              <SearchableSelect
                options={companyOptions}
                value={f.watch('company_id')}
                onChange={(v) => {
                  const id = String(v)
                  if (id === f.getValues('company_id')) return
                  f.clearErrors('company_id')
                  f.setValue('company_id', id, { shouldValidate: true, shouldDirty: true })
                  if (!propertyLocked) {
                    f.clearErrors('property_id')
                    f.setValue('property_id', '', { shouldValidate: true, shouldDirty: true })
                  }
                  companySelected(id)
                }}
                placeholder="Company *"
                searchPlaceholder="Search By Company Name"
                onSearch={searchCompany}
                searchDebounceMs={400}
                immediateSearchOnPaste
                onOpen={() => loadCompany('')}
                invokeSearchOnClose={false}
                error={f.formState.errors.company_id?.message as string | undefined}
                disabled={formBusy}
                triggerDataTestId="yardi-guest-search-company-select"
              />
            </div>
          </div>

          <div
            className={`space-y-1 ${propertyLocked ? 'pointer-events-none opacity-70' : ''}`}
            data-testid="yardi-guest-search-filters-property-field"
          >
            <label
              className="text-[11px] font-semibold tracking-wide text-slate-500 uppercase"
              data-testid="yardi-guest-search-filters-property-label"
            >
              Property
            </label>
            <div className="relative pill-filter-wrapper-vertical">
              <SearchableSelect
                options={propertyOptions}
                value={f.watch('property_id')}
                onChange={(v) => {
                  const id = String(v)
                  if (id === f.getValues('property_id')) return
                  f.clearErrors('property_id')
                  f.setValue('property_id', id, { shouldValidate: true, shouldDirty: true })
                }}
                placeholder="Property *"
                searchPlaceholder="Search By Property Name, City, State"
                onSearch={searchProperty}
                searchDebounceMs={400}
                immediateSearchOnPaste
                onOpen={() => loadProperty('')}
                invokeSearchOnClose={false}
                error={f.formState.errors.property_id?.message as string | undefined}
                disabled={formBusy}
                triggerDataTestId="yardi-guest-search-property-select"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label
              className="text-[11px] font-semibold tracking-wide text-slate-500 uppercase"
              data-testid="yardi-guest-search-filters-first-name-label"
            >
              First name
            </label>
            <input
              type="text"
              {...f.register('first_name')}
              disabled={formBusy}
              data-testid="yardi-guest-search-first-name-input"
              className={FILTER_TEXT_INPUT_CLASS}
              placeholder="First Name"
            />
            {f.formState.errors.first_name?.message && (
              <GuestSearchFieldError
                message={String(f.formState.errors.first_name.message)}
                testId="yardi-guest-search-first-name-error"
              />
            )}
          </div>

          <div className="space-y-1">
            <label
              className="text-[11px] font-semibold tracking-wide text-slate-500 uppercase"
              data-testid="yardi-guest-search-filters-last-name-label"
            >
              Last name
            </label>
            <input
              type="text"
              {...f.register('last_name')}
              disabled={formBusy}
              data-testid="yardi-guest-search-last-name-input"
              className={FILTER_TEXT_INPUT_CLASS}
              placeholder="Last Name"
            />
            {f.formState.errors.last_name?.message && (
              <GuestSearchFieldError
                message={String(f.formState.errors.last_name.message)}
                testId="yardi-guest-search-last-name-error"
              />
            )}
          </div>

          <div className="space-y-1">
            <label
              className="text-[11px] font-semibold tracking-wide text-slate-500 uppercase"
              data-testid="yardi-guest-search-filters-email-label"
            >
              Email
            </label>
            <input
              type="text"
              {...f.register('email_address')}
              disabled={formBusy}
              data-testid="yardi-guest-search-email-input"
              className={FILTER_TEXT_INPUT_CLASS}
              placeholder="Email"
            />
            {f.formState.errors.email_address?.message && (
              <GuestSearchFieldError
                message={String(f.formState.errors.email_address.message)}
                testId="yardi-guest-search-email-error"
              />
            )}
          </div>

          <div className="space-y-1">
            <label
              className="text-[11px] font-semibold tracking-wide text-slate-500 uppercase"
              data-testid="yardi-guest-search-filters-phone-label"
            >
              Phone
            </label>
            <Controller
              name="phone_number"
              control={f.control}
              render={({ field }) => (
                <PhoneInput
                  value={field.value || ''}
                  onChange={(cleaned) => {
                    field.onChange(cleaned)
                    void f.trigger('phone_number')
                  }}
                  onBlur={() => {
                    field.onBlur()
                    void f.trigger('phone_number')
                  }}
                  error={f.formState.errors.phone_number?.message as string | undefined}
                  showErrorMessage={false}
                  className={FILTER_TEXT_INPUT_CLASS}
                  data-testid="yardi-guest-search-phone-input"
                  disabled={formBusy}
                />
              )}
            />
            {f.formState.errors.phone_number?.message && (
              <GuestSearchFieldError
                message={String(f.formState.errors.phone_number.message)}
                testId="yardi-guest-search-phone-input-error"
              />
            )}
          </div>

          <div className="space-y-1">
            <label
              className="text-[11px] font-semibold tracking-wide text-slate-500 uppercase"
              data-testid="yardi-guest-search-filters-third-party-id-label"
            >
              Prospect ID
            </label>
            <input
              type="text"
              {...f.register('third_party_id')}
              disabled={formBusy}
              data-testid="yardi-guest-search-third-party-id-input"
              className={FILTER_TEXT_INPUT_CLASS}
              placeholder="Prospect ID"
            />
          </div>

          <div className="space-y-1">
            <label
              className="text-[11px] font-semibold tracking-wide text-slate-500 uppercase"
              data-testid="yardi-guest-search-filters-federal-id-label"
            >
              Government ID
            </label>
            <input
              type="text"
              {...f.register('federal_id')}
              disabled={formBusy}
              data-testid="yardi-guest-search-federal-id-input"
              className={FILTER_TEXT_INPUT_CLASS}
              placeholder="Government ID"
            />
          </div>

          <div className="space-y-1" data-testid="yardi-guest-search-filters-dob-field">
            <label
              className="text-[11px] font-semibold tracking-wide text-slate-500 uppercase"
              data-testid="yardi-guest-search-filters-dob-label"
            >
              Date of birth
            </label>
            <div className="yardi-guest-search-dob-field">
              <Controller
                name="date_of_birth"
                control={f.control}
                render={({ field }) => (
                  <DatePicker
                    id="yardi-guest-date_of_birth"
                    name="date_of_birth"
                    dataTestId="yardi-guest-search-dob"
                    value={field.value || ''}
                    onChange={(v) => {
                      field.onChange(v)
                      void f.trigger('date_of_birth')
                    }}
                    onBlur={field.onBlur}
                    placeholder="MM/DD/YYYY"
                    showHelperText={false}
                    popperFixed
                    className={FILTER_TEXT_INPUT_CLASS}
                    error={f.formState.errors.date_of_birth?.message as string | undefined}
                    disabled={formBusy}
                  />
                )}
              />
            </div>
          </div>

          <div
            className="self-end"
            data-testid="yardi-guest-search-join-filters-and-wrap"
          >
            <label
              className={cn(
                'flex min-h-[40px] w-full items-center gap-2 text-sm text-slate-800',
                formBusy ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
              )}
              data-testid="yardi-guest-search-join-filters-and-label"
            >
              <Controller
                name="join_filter_items_with_and"
                control={f.control}
                render={({ field }) => (
                  <input
                    type="checkbox"
                    data-testid="yardi-guest-search-join-filters-and-checkbox"
                    checked={field.value}
                    disabled={formBusy}
                    onChange={(e) => field.onChange(e.target.checked)}
                    className="h-4 w-4 shrink-0 rounded border-slate-300 text-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)]"
                  />
                )}
              />
              <span className="leading-snug">Join filter items with AND</span>
            </label>
          </div>

        </div>

        {/* Actions — same surface as fields (no full-width gray strip) */}
        <div
          className="border-t border-slate-100 pt-3 sm:pt-4"
          data-testid="yardi-guest-search-form-footer"
        >
          <div
            className="flex flex-wrap items-center justify-end gap-2 sm:gap-3"
            data-testid="yardi-guest-search-form-actions"
          >
            <button
              type="button"
              data-testid="yardi-guest-search-reset-all-button"
              disabled={formBusy}
              onClick={() => resetFilter()}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800 disabled:pointer-events-none disabled:opacity-50"
            >
              Reset
            </button>
            <button
              type="submit"
              data-testid="yardi-guest-search-done-button"
              disabled={formBusy}
              className="rounded-lg bg-[rgb(21_151_255)] px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-[rgba(21,151,255,0.22)] transition-all hover:bg-[#0d7acc] disabled:pointer-events-none disabled:opacity-50"
            >
              Go
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
